import get from 'lodash/get';

import waRequest from './wa-request';

const cancelablePersonGeoSingleton = waRequest.createCancelableSingleton();

export const baseApi = '/alsat/v2';

function getAlsatPersonGeodataConfigPattern({
  params: {organizationId, zoom, maxLng, maxLat, minLng, minLat, skipClustering, previousResultId},
  data: {
    dateStart,
    dateEnd,
    locationIds = undefined,
    personLocationTypes,
    mobileLocationScope,
    organizationIds = undefined,
    includeSubOrganizations = undefined,
    airlineId = undefined,
    airlineIds = undefined,
    airportId = undefined,
    flightNumberLike = undefined,
    flightNumbers = undefined,
    addressLabels = undefined,
    addressAccuracies = undefined,
    railwayNameLike = undefined,
    trainNumberLike = undefined,
  },
  signal,
}) {
  const url = `${baseApi}/persons/locations/cluster-searches`;

  return waRequest.requestAuthenticated({
    url: url,
    signal,
    method: 'post',
    params: {
      organizationId,
      zoom,
      maxLng,
      maxLat,
      minLng,
      minLat,
      skipClustering,
      previousResultId,
    },
    data: {
      organizationIds: organizationIds || [organizationId],
      includeSubOrganizations,
      dateStart: dateStart,
      dateEnd: dateEnd,
      locationIds: locationIds,
      personLocationTypes: personLocationTypes,
      mobileLocationScope: mobileLocationScope,
      airlineId: airlineId,
      airlineIds,
      airportId: airportId,
      flightNumberLike: flightNumberLike,
      flightNumbers,
      railwayNameLike: railwayNameLike,
      trainNumberLike: trainNumberLike,
      addressLabels,
      addressAccuracies,
    },
  });
}

function getAlsatPersonGeodata({
  organizationId,
  zoom,
  dateStart,
  dateEnd,
  locationIds = undefined,
  maxLng,
  maxLat,
  minLng,
  minLat,
  personLocationTypes,
  mobileLocationScope,
  organizationIds = undefined,
  airlineId = undefined,
  airlineIds = undefined,
  airportId = undefined,
  flightNumberLike = undefined,
  flightNumbers = undefined,
  addressLabels = undefined,
  addressAccuracies = undefined,
  railwayNameLike = undefined,
  trainNumberLike = undefined,
}) {
  const url = `${baseApi}/persons/locations/cluster-searches`;

  cancelablePersonGeoSingleton.cancel();

  return cancelablePersonGeoSingleton.requestAuthenticated({
    url: url,
    method: 'post',
    params: {
      organizationId,
      zoom,
      maxLng,
      maxLat,
      minLng,
      minLat,
    },
    data: {
      organizationIds: organizationIds || [organizationId],
      dateStart: dateStart,
      dateEnd: dateEnd,
      locationIds: locationIds,
      personLocationTypes: personLocationTypes,
      mobileLocationScope: mobileLocationScope,
      airlineId: airlineId,
      airlineIds,
      airportId: airportId,
      flightNumberLike: flightNumberLike,
      flightNumbers,
      railwayNameLike: railwayNameLike,
      trainNumberLike: trainNumberLike,
      addressLabels,
      addressAccuracies,
    },
  });
}

function getAlsatPersonsLocationsSearches({params, data, signal}) {
  const url = `${baseApi}/persons/locations/searches`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    params,
    data,
    signal,
  });
}

function initiatePersonExport({params, locale, data}) {
  const url = `${baseApi}/persons/exports`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    params: {...params, locale},
    data,
  });
}

function getSiteExportStatusConfigPattern({id, successCallback}) {
  const url = `${baseApi}/persons/exports/${id}`;

  return waRequest.requestWithRetryUntilSuccess(
    {
      url: url,
      method: 'get',
    },
    successCallback,
  );
}

function getPersonExportStatus(exportId, locale) {
  const url = `${baseApi}/persons/exports/${exportId}`;

  const requestConfig = {
    url: url,
    method: 'get',
    params: {
      locale,
    },
  };

  const successCallback = (result) => {
    const status = get(result, 'data.status.id');

    switch (status) {
      case 'COMPLETE':
        return true;
      case 'NEW':
      case 'PROCESSING':
        return false;
      case 'FAILED':
      case 'CANCELED':
      case 'DELETED':
      case 'TOO_LARGE':
      case 'DOWNLOADED':
        throw new Error('Error occurred while generating download');
      default:
        return false;
    }
  };

  return waRequest.requestWithRetryUntilSuccess(requestConfig, successCallback, 2000);
}

function getPersonExportFile(exportId, markDownloaded = true) {
  const url = `${baseApi}/persons/exports/${exportId}/file`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'get',
    responseType: 'blob',
    params: {
      markDownloaded,
    },
  });
}

function getAlsatPersonsLocationsRollupSearchesConfigPattern({data, params, signal}) {
  const url = `${baseApi}/persons/locations/rollup-searches`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    params,
    data,
    signal,
  });
}

function getPersonLocationSummaries({data, signal}) {
  const url = `${baseApi}/persons/locations/summaries`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    data,
    signal,
  });
}

/**
 * Initiate authenticated POST ${baseApi}/persons/searches
 * use this using the standard config pattern
 *
 * @param {Object} config
 * @param {Object} config.params
 * @param {number} config.params.page Zero-based page index (0..N)
 * @param {number} config.params.size The size of the page to be returned
 * @param config.params.sort Sorting criteria in the format: property,(asc|desc). Default sort order is ascending. Multiple sort criteria are supported.
 * @param config.params.includeDetails DEPRECATED, use detailLevel instead
 * @param config.params.locale The requested language to return data in.
 * @param config.params.detailLevel Controls which fields to return.
 * @param config.params.saveToHistory Boolean indicating if search should be considered a "recent" search
 * @param {Object} config.data Searching and filtering options for a person
 * @param {AbortSignal} config.signal use to cancel request
 * @returns {Promise}
 */
function getAlsatPersonsListConfigPattern({
  params: {saveToHistory, page = 0, size = 25, sort, locale, includeDetails, detailLevel, organizationId},
  signal,
  data,
}) {
  return waRequest.requestAuthenticated({
    url: `${baseApi}/persons/searches`,
    method: 'post',
    params: {
      saveToHistory,
      page,
      size,
      sort,
      locale,
      includeDetails,
      detailLevel,
      organizationId,
    },
    signal,
    data,
  });
}

function initiatePersonExportConfigPattern({params, data}) {
  const {
    organizationId,
    locale,
    createUrl,
    sort,
    accountInfo,
    includeOrganizations,
    addresses,
    emails,
    names,
    phones,
    optionalFields,
    mobileLocations,
    includePersonTypes,
    tripSegments,
    includeExpatriations,
    includeSites,
  } = params;

  return waRequest.requestAuthenticated({
    url: `${baseApi}/persons/exports`,
    method: 'post',
    params: {
      organizationId,
      locale,
      createUrl,
      sort,
      accountInfo,
      includeOrganizations,
      addresses,
      emails,
      names,
      phones,
      optionalFields,
      mobileLocations,
      includePersonTypes,
      tripSegments,
      includeExpatriations,
      includeSites,
    },
    data,
  });
}

function getPersonExportStatusConfigPattern({exportId, locale, successCallback}) {
  return waRequest.requestWithRetryUntilSuccess(
    {
      url: `${baseApi}/persons/exports/${exportId}`,
      method: 'get',
      params: {
        locale,
      },
    },
    successCallback,
    2000,
  );
}

function getPersonExportColumns({params, data}) {
  const {locale} = params || {};

  return waRequest.requestAuthenticated({
    url: `${baseApi}/persons/exports/columns/searches`,
    method: 'POST',
    params: {
      locale,
    },
    data,
  });
}

/**
 * Creates an advanced person export with custom column selection
 * @param {Object} config - Configuration object
 * @param {Object} config.params - Query parameters
 * @param {string} config.params.organizationId - Organization ID
 * @param {string} config.params.locale - User locale
 * @param {boolean} config.params.createUrl - Create URL flag
 * @param {Object} config.data - Export request data
 * @param {string[]} config.data.columns - Array of column IDs to export
 * @param {string} config.data.exportType - Export type (e.g., "PERSON_SIMPLE")
 * @param {Object} config.data.filter - PersonSearchFilter object
 * @returns {Promise} API response with export ID and status
 */
function initiateAdvancedPersonExportConfigPattern(config) {
  const {params, data} = config;

  const {organizationId, locale, createUrl} = params;

  return waRequest.requestAuthenticated({
    url: `${baseApi}/persons/advanced-exports`,
    method: 'post',
    params: {
      organizationId,
      locale,
      createUrl,
    },
    data,
  });
}

function createSavedSearch({data: {dateRangeType, description, filter, name, organizationId}}) {
  const url = `${baseApi}/persons/saved-searches`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'POST',
    data: {
      dateRangeType,
      description,
      filter,
      name,
      organizationId,
    },
  });
}

function updateSavedSearch({pathParams: {id}, data: {dateRangeType, description, filter, name, organizationId}}) {
  const url = `${baseApi}/persons/saved-searches/${id}`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'PUT',
    data: {
      dateRangeType,
      description,
      filter,
      name,
      organizationId,
    },
  });
}

function deleteSavedSearch({pathParams: {id}}) {
  const url = `${baseApi}/persons/saved-searches/${id}`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'DELETE',
  });
}

function getSavedSearches({params: {page = 0, size = 25, sort}, data}) {
  const url = `${baseApi}/persons/saved-searches/searches`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'POST',
    params: {
      page,
      size,
      sort,
    },
    data,
  });
}

function createSavedSearchSchedule({pathParams: {searchId}, data}) {
  const url = `${baseApi}/persons/saved-searches/${searchId}/schedules`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'POST',
    data,
  });
}

function updateSavedSearchSchedule({pathParams: {searchId, scheduleId}, data}) {
  const url = `${baseApi}/persons/saved-searches/${searchId}/schedules/${scheduleId}`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'PUT',
    data,
  });
}

function deleteSavedSearchSchedule({pathParams: {searchId, scheduleId}}) {
  const url = `${baseApi}/persons/saved-searches/${searchId}/schedules/${scheduleId}`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'DELETE',
  });
}

function getSavedSearchSchedule({pathParams: {searchId, id}, signal}) {
  const url = `${baseApi}/persons/saved-searches/${searchId}/schedules/${id}`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'GET',
    signal,
  });
}

function getPersonFilterFieldValues(config) {
  const {pathParams, ...restConfig} = config;
  const {filterField} = pathParams;

  return waRequest.requestAuthenticated({
    url: `${baseApi}/persons/filter-values/${filterField}/searches`,
    method: 'POST',
    ...restConfig,
  });
}

function getCustomPersonFilterFieldValues(config) {
  const {pathParams, ...restConfig} = config;
  const {filterField, fieldId} = pathParams;

  return waRequest.requestAuthenticated({
    url: `${baseApi}/persons/filter-values/${filterField}/${fieldId}/searches`,
    method: 'POST',
    ...restConfig,
  });
}

function getSearchTileGroups({params: {organizationId, locale}, signal}) {
  const url = `${baseApi}/persons/search-tile-groups`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'GET',
    params: {
      organizationId,
      locale,
    },
    signal,
  });
}

function getSavedSearchSchedules({pathParams: {clientId}, signal}) {
  const url = `${baseApi}/organizations/${clientId}/persons/saved-searches/schedules`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'GET',
    signal,
  });
}

export default {
  getAlsatPersonsLocationsSearches,
  getAlsatPersonGeodataConfigPattern,
  getAlsatPersonGeodata,
  initiatePersonExport,
  initiateAdvancedPersonExportConfigPattern,
  getPersonExportColumns,
  getPersonExportStatus,
  getPersonExportFile,
  getPersonLocationSummaries,
  getSiteExportStatusConfigPattern,
  getAlsatPersonsListConfigPattern,
  initiatePersonExportConfigPattern,
  getPersonExportStatusConfigPattern,
  createSavedSearch,
  updateSavedSearch,
  deleteSavedSearch,
  getSavedSearches,
  createSavedSearchSchedule,
  updateSavedSearchSchedule,
  deleteSavedSearchSchedule,
  getSavedSearchSchedule,
  getAlsatPersonsLocationsRollupSearchesConfigPattern,
  getPersonFilterFieldValues,
  getCustomPersonFilterFieldValues,
  getSearchTileGroups,
  getSavedSearchSchedules,
};
